import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import { Button, Container } from '@mui/material';

import OfferBreadcrumbs from '~/components/Common/Elements/OfferBreadcrumbs';
import PageHeader from '~/components/Common/Elements/PageHeader';
import VimeoWidget from '~/components/Common/Forms/widgets/VimeoWidget';
import Spinner from '~/components/Common/Spinner';

import ReservationService from '~/services/ReservationService';
import VendorsService from '~/services/VendorsService';

import { formatError } from '~/utils/errorFormatter';

import cleanProperty from './helpers/cleanProperty';

export default function VideosEditPage() {
  const { id_property: propertyId, id_vendor: vendorId } = useParams<{ id_property: string; id_vendor: string }>();

  const [property, setProperty] = useState(null);
  const [vendorName, setVendorName] = useState<string>('');
  const { enqueueSnackbar } = useSnackbar();
  const [leVimeoId, setLeVimeoId] = useState(null);
  const [whitelabelVimeoId, setWhitelabelVimeoId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([ReservationService.getProperty(propertyId), VendorsService.getVendorById(vendorId)])
      .then(([propFetch, vendorFetch]) => {
        setProperty(cleanProperty(propFetch.result));
        setVendorName(vendorFetch.result?.vendor_name || vendorFetch.result?.name || '');
        setLeVimeoId(propFetch.result.videos.luxuryescapes?.vimeo_id ?? '');
        setWhitelabelVimeoId(propFetch.result.videos.whitelabel?.vimeo_id ?? '');
        setLoading(false);
      })
      .catch((err) => {
        enqueueSnackbar(err.message, { variant: 'error' });
      });
  }, [enqueueSnackbar, propertyId, vendorId]);

  const doUpdate = useCallback(async () => {
    try {
      setIsSaving(true);
      await ReservationService.updateProperty(
        {
          ...property,
          videos: {
            luxuryescapes: {
              vimeo_id: leVimeoId || '',
            },
            whitelabel: {
              vimeo_id: whitelabelVimeoId || '',
            },
          },
        },
        property.id,
      );
      setIsSaving(false);
      enqueueSnackbar('Saved', {
        variant: 'success',
      });
    } catch (err) {
      setIsSaving(false);
      enqueueSnackbar(formatError(err), {
        variant: 'error',
      });
    }
  }, [enqueueSnackbar, leVimeoId, property, whitelabelVimeoId]);

  return (
    <Container maxWidth="xl">
      <OfferBreadcrumbs
        vendor={{ label: vendorName || 'Vendor', href: `/vendors/${vendorId}` }}
        property={{
          label: property?.name || 'Property',
          href: `/vendors/${vendorId}/properties/${propertyId}`,
        }}
        currentPage="Videos"
      />
      <PageHeader title="Edit property videos" />
      {loading && <div>Loading...</div>}
      {!loading && (
        <>
          <h3>Vimeo video (Luxury Escapes)</h3>
          <VimeoWidget
            value={leVimeoId}
            onChange={setLeVimeoId}
            previousValue={property.videos?.luxuryescapes?.vimeo_id}
          />

          <h3>Vimeo video (Whitelabel Escapes)</h3>
          <VimeoWidget
            value={whitelabelVimeoId}
            onChange={setWhitelabelVimeoId}
            previousValue={property.videos?.whitelabel?.vimeo_id}
          />

          <Button sx={{ width: 100 }} onClick={doUpdate} variant="contained">
            {isSaving ? <Spinner size={20} /> : 'Save'}
          </Button>
        </>
      )}
    </Container>
  );
}
